/**
 * Myop Loader - Shared loader/fallback UI for host frameworks
 * Framework-agnostic implementation using pure DOM
 */

// Myop Logo SVG path
const MYOP_LOGO_PATH =
  "M151.438 2.7168C177.625 2.76042 248.179 4.2832 300.4 56.7441C350.671 107.245 364.44 114.452 389.967 119.211C392.576 119.436 397.472 119.394 400.802 116.521C403.011 114.614 403.921 109.518 404.265 104.627V5.95996C404.265 4.85556 405.16 3.96023 406.265 3.95996H522.711C523.815 3.95996 524.711 4.85539 524.711 5.95996V122.405C524.711 123.51 523.815 124.405 522.711 124.405H421.998C418.579 124.728 413.852 125.627 410.974 128.095C407.879 130.748 407.042 137.805 406.845 142.661V240.414C406.845 241.436 406.111 242.292 405.093 242.38C366.548 245.717 277.079 228.462 235.837 173.668C202.909 129.919 173.882 122.474 151.458 122.077C150.354 122.058 149.459 121.164 149.459 120.06V4.71582C149.459 3.6114 150.334 2.71519 151.438 2.7168ZM62.2031 0C96.5566 0.000213148 124.405 27.8496 124.405 62.2031C124.405 96.5564 96.5564 124.405 62.2031 124.405C27.8496 124.405 0.000213175 96.5566 0 62.2031C0 27.8495 27.8495 0 62.2031 0Z";

// Style constants
export const LOADER_GRADIENT =
  "linear-gradient(135deg, #FF8651 0%, #FFBEA8 50%, #A397F5 100%)";
export const FALLBACK_GRADIENT =
  "linear-gradient(135deg, #9CA3AF 0%, #D1D5DB 50%, #E5E7EB 100%)";

/**
 * Creates the Myop logo SVG element
 */
export function createMyopLogoSvg(): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", "80");
  svg.setAttribute("viewBox", "0 0 525 243");
  svg.setAttribute("fill", "none");

  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", MYOP_LOGO_PATH);
  path.setAttribute("fill", "white");

  svg.appendChild(path);
  return svg;
}

/**
 * Returns the inline HTML string for the Myop logo SVG
 */
export function getMyopLogoHtml(): string {
  return `<svg width="80" viewBox="0 0 525 243" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="${MYOP_LOGO_PATH}" fill="white"/>
  </svg>`;
}

/**
 * Returns the inline styles for the loader container
 */
export function getLoaderStyles(fadeDuration: number = 200): string {
  return `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    background: ${LOADER_GRADIENT};
    display: flex;
    justify-content: center;
    align-items: center;
    transition: opacity ${fadeDuration}ms ease-out;
  `;
}

/**
 * Returns the inline styles for the fallback container
 */
export function getFallbackStyles(): string {
  return `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${FALLBACK_GRADIENT};
    display: flex;
    justify-content: center;
    align-items: center;
  `;
}

/**
 * Creates a loader DOM element with the Myop branding
 */
export function createLoaderElement(fadeDuration: number = 200): HTMLDivElement {
  const loader = document.createElement("div");
  loader.className = "myop-loader";
  loader.style.cssText = getLoaderStyles(fadeDuration);
  loader.appendChild(createMyopLogoSvg());
  return loader;
}

/**
 * Creates a fallback DOM element with the Myop branding
 */
export function createFallbackElement(): HTMLDivElement {
  const fallback = document.createElement("div");
  fallback.className = "myop-fallback";
  fallback.style.cssText = getFallbackStyles();
  fallback.appendChild(createMyopLogoSvg());
  return fallback;
}

/**
 * Returns the complete loader HTML as a string (for use in templates)
 */
export function getLoaderHtml(fadeDuration: number = 200): string {
  return `<div class="myop-loader" style="${getLoaderStyles(fadeDuration).replace(/\n/g, " ").replace(/\s+/g, " ").trim()}">
    ${getMyopLogoHtml()}
  </div>`;
}

/**
 * Returns the complete fallback HTML as a string (for use in templates)
 */
export function getFallbackHtml(): string {
  return `<div class="myop-fallback" style="${getFallbackStyles().replace(/\n/g, " ").replace(/\s+/g, " ").trim()}">
    ${getMyopLogoHtml()}
  </div>`;
}

/**
 * Interface for loader options used by host frameworks
 */
export interface MyopLoaderOptions {
  /** Fade duration in milliseconds (default: 200) */
  fadeDuration?: number;
  /** Initial opacity (default: 1) */
  opacity?: number;
}

/**
 * CSS-in-JS style objects for frameworks that support them
 */
export const loaderStyleObject = {
  position: "absolute" as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  background: LOADER_GRADIENT,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

export const fallbackStyleObject = {
  position: "absolute" as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: FALLBACK_GRADIENT,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

export const containerStyleObject = {
  position: "absolute" as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};
